import { forwardRef, type InputHTMLAttributes, createContext, useContext } from 'react';
import { cn } from '@/utils';

interface RadioGroupContextValue {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue>({});

interface SJRadioGroupProps {
  children: React.ReactNode;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  orientation?: 'vertical' | 'horizontal';
  disabled?: boolean;
  className?: string;
}

export function SJRadioGroup({ children, name, value, onChange, orientation = 'vertical', disabled, className }: SJRadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, disabled }}>
      <div
        role="radiogroup"
        className={cn(
          'flex gap-3',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
          className
        )}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

export interface SJRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string;
  description?: string;
  radioValue: string;
}

export const SJRadio = forwardRef<HTMLInputElement, SJRadioProps>(
  ({ className, label, description, radioValue, disabled: propDisabled, id, ...props }, ref) => {
    const group = useContext(RadioGroupContext);
    const isChecked = group.value === radioValue;
    const isDisabled = propDisabled ?? group.disabled;

    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex items-start gap-3 cursor-pointer',
          isDisabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <div className="relative flex items-center justify-center pt-0.5">
          <input
            ref={ref}
            type="radio"
            id={id}
            name={group.name}
            value={radioValue}
            checked={isChecked}
            disabled={isDisabled}
            onChange={() => group.onChange?.(radioValue)}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full border-2 border-[var(--sj-border)] bg-[var(--sj-surface)] transition-all',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',
              isChecked && 'border-primary-600'
            )}
          >
            {isChecked && <div className="h-2.5 w-2.5 rounded-full bg-primary-600" />}
          </div>
        </div>
        {(label || description) && (
          <div className="flex flex-col">
            {label && <span className="text-sm font-medium text-[var(--sj-text-primary)]">{label}</span>}
            {description && <span className="text-xs text-[var(--sj-text-muted)]">{description}</span>}
          </div>
        )}
      </label>
    );
  }
);

SJRadio.displayName = 'SJRadio';
